import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RotateCw, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CircleAvatar } from "@/components/CircleAvatar";
import { api } from "@/lib/api";
import { userCoords } from "@/hooks/useGeo";
import { useAuth } from "@/context/AuthContext";
import { normRecipient, money, PRESET_AMOUNTS, tierColor, initials, UNLIMITED_RADIUS } from "@/lib/blessed";
import { celebrate } from "@/lib/effects";

const TITLES = {
  wheel: { name: "Spin the Wheel", emoji: "🎡" },
  slots: { name: "Slots", emoji: "🎰" },
  blackjack: { name: "Blackjack", emoji: "🃏" },
  horse: { name: "Horse Race", emoji: "🐎" },
  deal: { name: "Deal or No Deal", emoji: "💼" },
};

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

// Radius label for the neighbor picker (reads the same value the Map persists).
const getRadiusLabel = () => {
  const stored = localStorage.getItem("blessed_radius");
  const rad = stored !== null ? JSON.parse(stored) : 100;
  return rad == null ? "any distance" : `${rad} mi`;
};

// ---------- Amount picker (post-win) — shared across all mini-games ----------
export const AmountModal = ({ open, recipient, onClose, onPick }) => {
  const [custom, setCustom] = useState("");
  if (!open) return null;
  const name = recipient?.name?.split(" ")[0] || "";
  const sendCustom = () => {
    const v = Number(custom);
    if (!v || v < 5) { toast.error("The minimum blessing is $5."); return; }
    onPick(v);
  };
  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()} className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-6" data-testid="game-amount-modal">
      <div className="relative bg-surface-2 rounded-lg p-6 pt-14 w-full max-w-sm animate-pop-in">
        <button data-testid="amount-modal-close" onClick={onClose} className="absolute left-4 top-4 w-9 h-9 rounded-pill grid place-items-center bg-surface-3 text-ink-tertiary">
          <ArrowLeft size={18} />
        </button>
        <div className="flex flex-col items-center mb-1">
          <CircleAvatar src={recipient?.picture} name={recipient?.name} size={60} ring="linear-gradient(135deg,#1E40AF,#8B5CF6)" className="mb-3" />
          <h3 className="font-black text-xl text-center">Bless {name}</h3>
        </div>
        <p className="text-center text-ink-tertiary text-sm mb-5">Pick an amount to send</p>
        <div className="grid grid-cols-3 gap-3">
          {PRESET_AMOUNTS.map((a) => (
            <button key={a} data-testid={`game-amt-${a}`} onClick={() => onPick(a)}
              className="py-4 rounded-md font-black text-white active:scale-95 transition-transform" style={{ background: tierColor(a) }}>{money(a)}</button>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <input data-testid="game-amt-custom-input" type="number" inputMode="numeric" value={custom}
            onChange={(e) => setCustom(e.target.value)} placeholder="Custom $"
            className="flex-1 h-12 px-4 rounded-md bg-surface border border-border font-black text-ink outline-none" />
          <Button data-testid="game-amt-custom-send" onClick={sendCustom} className="h-12 px-6 rounded-md font-black bg-brand-primary text-white">Send</Button>
        </div>
      </div>
    </div>
  );
};

// ---------- Spin the Wheel ----------
const SLICES = [
  { amt: 5, color: "#EF4444" }, { amt: 10, color: "#F59E0B" }, { amt: 20, color: "#FFD93D" },
  { amt: 40, color: "#22C55E" }, { amt: 50, color: "#3B82F6" }, { amt: 100, color: "#8B5CF6" },
];

// ---------- Neighbor picker (tap the wheel avatar to change recipient) ----------
const NeighborPicker = ({ open, onClose, pool, recipient, onPick, radiusLabel }) => {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return pool;
    return pool.filter((p) => (p?.name || "").toLowerCase().includes(s) || (p?.place || "").toLowerCase().includes(s));
  }, [q, pool]);
  if (!open) return null;
  return (
    <div onClick={onClose} className="fixed inset-0 z-[70] bg-black/55 flex items-end justify-center" data-testid="neighbor-picker">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-surface rounded-t-3xl px-5 pt-2 pb-5 flex flex-col animate-slide-up" style={{ maxHeight: "82vh", minHeight: "60vh" }}>
        <div className="w-10 h-1 rounded-full bg-ink/20 mx-auto mb-2.5" />
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-black text-ink">Pick a neighbor</h3>
          <button data-testid="neighbor-picker-close" onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-pill grid place-items-center bg-surface-3 border border-border text-ink"><X size={18} /></button>
        </div>
        <p className="text-sm text-ink-tertiary mt-1 mb-3">{pool.length} {pool.length === 1 ? "person" : "people"} within {radiusLabel}</p>
        <div className="flex items-center gap-2 border border-border rounded-pill px-4 h-11 mb-3 bg-surface-2">
          <Search size={18} className="text-ink-tertiary shrink-0" />
          <input data-testid="neighbor-picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or place" autoCorrect="off" autoCapitalize="none"
            className="flex-1 bg-transparent outline-none text-[15px] text-ink" />
          {q && <button onClick={() => setQ("")} className="text-ink-tertiary"><X size={16} /></button>}
        </div>
        <div className="overflow-y-auto flex-1 -mx-1 px-1">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-ink-tertiary">
              {pool.length === 0 ? "No neighbors in your radius yet. Widen your map radius." : "No matches. Try a different search."}
            </div>
          ) : filtered.map((item, i) => {
            const isCurrent = recipient?.id === item.id;
            const noBank = item.payoutsEnabled === false;
            const sub = [item.place, item.distance != null ? `${item.distance} mi` : null, noBank ? "no bank linked" : null].filter(Boolean).join(" · ");
            return (
              <button key={item.id} data-testid={`neighbor-picker-row-${item.id}`} onClick={() => onPick(item)}
                className={`flex items-center gap-3 w-full py-3 text-left ${i === 0 ? "" : "border-t border-border"}`}>
                <CircleAvatar src={item.picture} name={item.name} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-black text-ink truncate">{item.name}{isCurrent ? "  ✓" : ""}</div>
                  {sub && <div className="text-xs text-ink-tertiary mt-0.5 truncate">{sub}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Wheel = ({ neighbors, onWin }) => {
  const [recipient, setRecipient] = useState(() => neighbors[Math.floor(Math.random() * Math.max(1, neighbors.length))]);
  const [rot, setRot] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [multiMode, setMultiMode] = useState(false);
  const [multiPeople, setMultiPeople] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const canMulti = neighbors.length >= 6;
  const grad = `conic-gradient(from -30deg, ${SLICES.map((s, i) => `${s.color} ${i * 60}deg ${(i + 1) * 60}deg`).join(", ")})`;

  const radiusLabel = (() => {
    const stored = localStorage.getItem("blessed_radius");
    const rad = stored !== null ? JSON.parse(stored) : 100;
    return rad == null ? "any distance" : `${rad} mi`;
  })();

  const toggleMulti = () => {
    if (spinning) return;
    if (!multiMode) {
      if (!canMulti) { toast.error("Need at least 6 neighbors in your radius for Multiple mode. Widen your radius on the Map."); return; }
      setMultiPeople(shuffle(neighbors).slice(0, 6));
    }
    setMultiMode((v) => !v);
  };

  const spin = () => {
    if (spinning) return;
    if (multiMode && multiPeople.length < 6) return;
    if (!multiMode && !recipient) return;
    setSpinning(true);
    const w = Math.floor(Math.random() * SLICES.length);
    const final = 360 * 6 - w * 60;
    setRot(final);
    setTimeout(() => {
      celebrate();
      if (multiMode) onWin(multiPeople[w]);   // no amount → opens AmountModal for that person
      else onWin(recipient, SLICES[w].amt);   // amounts mode → straight to donate
      setSpinning(false);
    }, 3800);
  };

  return (
    <div className="flex flex-col items-center px-6 pt-6">
      <button data-testid="wheel-multi-toggle" onClick={toggleMulti} disabled={spinning}
        className={`mb-4 px-4 py-2 rounded-pill font-black text-sm border transition-colors disabled:opacity-50 ${multiMode ? "bg-brand-primary border-brand-primary text-white" : "bg-surface-2 border-border text-ink"}`}>
        {multiMode ? "✓ Multiple mode (6 people)" : "Multiple mode"}
      </button>
      <div className="relative" style={{ width: 320, height: 320 }}>
        <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-20" style={{ width: 0, height: 0, borderLeft: "14px solid transparent", borderRight: "14px solid transparent", borderTop: "22px solid #050A1F" }} />
        <div className="w-full h-full rounded-pill border-4 border-ink relative" style={{ background: grad, transform: `rotate(${rot}deg)`, transition: "transform 3.6s cubic-bezier(0.17,0.67,0.32,1)" }}>
          {SLICES.map((s, i) => (
            <div key={i} className={`absolute left-1/2 top-1/2 font-black text-ink ${multiMode ? "text-base" : "text-2xl"}`} style={{ transform: `translate(-50%,-50%) rotate(${i * 60}deg) translateY(-115px) rotate(${-i * 60}deg)` }}>
              {multiMode ? (multiPeople[i]?.name || "?").split(" ")[0].slice(0, 8) : money(s.amt)}
            </div>
          ))}
        </div>
        {!multiMode ? (
          <button data-testid="wheel-open-picker" onClick={() => { if (!spinning) setPickerOpen(true); }} disabled={spinning}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 disabled:cursor-default" style={{ cursor: spinning ? "default" : "pointer" }}>
            <CircleAvatar src={recipient?.picture} name={recipient?.name} size={96} ring="#ffffff" className="shadow-2xl" />
            {!spinning && (
              <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap px-2.5 py-0.5 rounded-pill text-[10px] font-extrabold text-white" style={{ background: "#1B1233" }}>Tap to change</span>
            )}
          </button>
        ) : (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <CircleAvatar src={multiPeople[0]?.picture} name="?" size={96} ring="#ffffff" className="shadow-2xl opacity-90" />
          </div>
        )}
      </div>
      <h2 className="text-2xl font-black mt-6">{multiMode ? "Spin for a neighbor" : recipient?.name}</h2>
      <p className="text-center text-ink-tertiary font-semibold mt-2 max-w-sm">
        {multiMode
          ? "Spin to land on a neighbor, then choose how much to bless them."
          : `Spin to land on an amount, then send it straight to ${recipient?.name?.split(" ")[0]}.`}
      </p>
      <Button data-testid="wheel-spin" disabled={spinning} onClick={spin} className="w-full max-w-md h-14 mt-6 rounded-pill font-black text-lg bg-brand-primary text-white">
        {spinning ? "Spinning…" : "Spin the wheel 🎡"}
      </Button>

      <NeighborPicker open={pickerOpen} onClose={() => setPickerOpen(false)} pool={neighbors} recipient={recipient} radiusLabel={radiusLabel}
        onPick={(item) => { setRecipient(item); setPickerOpen(false); }} />
    </div>
  );
};

// ---------- Slots ----------
const Slots = ({ neighbors, onWin }) => {
  const [pool, setPool] = useState(() => neighbors.slice(0, 4));
  const [grid, setGrid] = useState(() => Array.from({ length: 9 }, (_, i) => pool[i % Math.max(1, pool.length)]));
  const [spinning, setSpinning] = useState(false);
  const [pickFor, setPickFor] = useState(null); // index in pool being changed

  const spin = () => {
    if (spinning || pool.length === 0) return;
    setSpinning(true);
    let ticks = 0;
    const iv = setInterval(() => {
      setGrid(Array.from({ length: 9 }, () => pool[Math.floor(Math.random() * pool.length)]));
      if (++ticks > 12) {
        clearInterval(iv);
        const final = Array.from({ length: 9 }, () => pool[Math.floor(Math.random() * pool.length)]);
        setGrid(final);
        setSpinning(false);
        const lines = [[0,1,2],[3,4,5],[6,7,8],[0,3,6],[1,4,7],[2,5,8]];
        const win = lines.find((l) => final[l[0]]?.id === final[l[1]]?.id && final[l[1]]?.id === final[l[2]]?.id);
        if (win) { celebrate(); onWin(final[win[0]]); }
        else toast.info("No match — spin again!");
      }
    }, 90);
  };

  return (
    <div className="px-4 pt-5">
      <p className="text-center font-bold text-ink mb-3">Match 3 in a row or column → send them a gift 💛</p>
      {/* Players in the pool — tap any to swap them */}
      <div className="flex items-center justify-center gap-3 mb-4">
        {pool.map((n, i) => (
          <button key={i} data-testid={`slots-player-${i}`} disabled={spinning} onClick={() => setPickFor(i)}
            className="relative disabled:cursor-default" style={{ cursor: spinning ? "default" : "pointer" }}>
            <CircleAvatar src={n?.picture} name={n?.name} size={48} ring="#ffffff" />
            {!spinning && <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-4 rounded-pill grid place-items-center bg-brand-primary text-white text-[10px] font-black">✎</span>}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3 p-3 rounded-lg mx-auto max-w-sm" style={{ background: "var(--brand-tertiary)" }}>
        {grid.map((n, i) => (
          <div key={i} className="aspect-square rounded-md bg-white p-1 shadow overflow-hidden">
            {n?.picture ? (
              <img src={n.picture} alt="" className="w-full h-full object-cover rounded" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-full h-full rounded grid place-items-center text-white font-black text-2xl" style={{ background: "linear-gradient(135deg,#1E40AF,#5B21B6)" }}>{initials(n?.name)}</div>
            )}
          </div>
        ))}
      </div>
      <Button data-testid="slots-spin" disabled={spinning} onClick={spin} className="w-full max-w-sm mx-auto flex h-14 mt-5 rounded-pill font-black text-lg bg-brand-primary text-white">
        {spinning ? "Spinning…" : "SPIN 🎰"}
      </Button>
      <p className="text-center text-ink-tertiary font-semibold mt-4">Pool of {pool.length} neighbors. Tap a face above to swap who's in the game.</p>

      <NeighborPicker open={pickFor !== null} onClose={() => setPickFor(null)} pool={neighbors} recipient={pool[pickFor]} radiusLabel={getRadiusLabel()}
        onPick={(item) => { setPool((p) => p.map((x, i) => (i === pickFor ? item : x))); setPickFor(null); }} />
    </div>
  );
};

// ---------- Blackjack ----------
const RANKS = ["A","2","3","4","5","6","7","8","9","10","J","Q","K"];
const SUITS = ["♠","♥","♦","♣"];
const draw = () => ({ r: RANKS[Math.floor(Math.random() * RANKS.length)], s: SUITS[Math.floor(Math.random() * SUITS.length)] });
const handValue = (cards) => {
  let total = 0, aces = 0;
  cards.forEach((c) => { if (c.r === "A") { total += 11; aces++; } else if (["K","Q","J"].includes(c.r)) total += 10; else total += Number(c.r); });
  while (total > 21 && aces > 0) { total -= 10; aces--; }
  return total;
};
const Card = ({ c, hidden }) => (
  <div className={`w-20 h-28 rounded-md border-2 grid place-items-center font-black text-2xl ${hidden ? "" : "bg-white border-border"}`}
    style={hidden ? { background: "#4C1D95", borderColor: "#4C1D95" } : { color: ["♥","♦"].includes(c.s) ? "#EF4444" : "#050A1F" }}>
    {hidden ? "" : <div className="text-center leading-none"><div>{c.r}</div><div className="text-3xl">{c.s}</div></div>}
  </div>
);

const Blackjack = ({ neighbors, targetIdx, onWin }) => {
  const [target, setTarget] = useState(() => neighbors[targetIdx % neighbors.length]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [player, setPlayer] = useState([draw(), draw()]);
  const [dealer, setDealer] = useState([draw(), draw()]);
  const [stood, setStood] = useState(false);
  const pv = handValue(player);

  const hit = () => { if (stood) return; const np = [...player, draw()]; setPlayer(np); if (handValue(np) > 21) finish(np, dealer); };
  const stand = () => {
    let d = [...dealer];
    while (handValue(d) < 17) d.push(draw());
    setDealer(d); setStood(true); finish(player, d);
  };
  const reset = () => { setPlayer([draw(), draw()]); setDealer([draw(), draw()]); setStood(false); };
  const finish = (p, d) => {
    const pvv = handValue(p), dvv = handValue(d);
    const win = pvv <= 21 && (dvv > 21 || pvv > dvv);
    setTimeout(() => {
      if (win) { celebrate(); onWin(target); }
      else { toast.error(`Dealer wins (${dvv} vs ${pvv}). New hand!`); reset(); }
    }, win ? 500 : 1300);
  };

  return (
    <div className="px-4 pt-5">
      <button data-testid="bj-open-picker" onClick={() => !stood && setPickerOpen(true)} disabled={stood}
        className="w-full rounded-lg p-4 mb-5 flex items-center gap-3 text-left disabled:cursor-default" style={{ background: "var(--brand-tertiary)" }}>
        <CircleAvatar src={target?.picture} name={target?.name} size={48} />
        <div className="flex-1 min-w-0"><div className="font-black text-brand-primary truncate">Playing to bless {target?.name}</div><div className="text-sm text-ink-tertiary">{stood ? "Beat the dealer, then pick an amount" : "Tap to change · beat the dealer to send"}</div></div>
      </button>
      <div className="text-xs font-black uppercase text-ink-tertiary mb-2">Dealer · {stood ? handValue(dealer) : `${handValue([dealer[0]])}?`}</div>
      <div className="flex gap-3 mb-6">{dealer.map((c, i) => <Card key={i} c={c} hidden={!stood && i === 1} />)}</div>
      <div className="text-xs font-black uppercase text-ink-tertiary mb-2">You · {pv}</div>
      <div className="flex gap-3 flex-wrap mb-6">{player.map((c, i) => <Card key={i} c={c} />)}</div>
      <div className="flex gap-3">
        <Button data-testid="bj-hit" disabled={stood || pv >= 21} onClick={hit} className="flex-1 h-14 rounded-pill font-black text-lg bg-brand-primary text-white">Hit</Button>
        <Button data-testid="bj-stand" disabled={stood} onClick={stand} variant="outline" className="flex-1 h-14 rounded-pill font-black text-lg border-border">Stand</Button>
      </div>

      <NeighborPicker open={pickerOpen} onClose={() => setPickerOpen(false)} pool={neighbors} recipient={target} radiusLabel={getRadiusLabel()}
        onPick={(item) => { setTarget(item); setPickerOpen(false); }} />
    </div>
  );
};

// ---------- Horse Race ----------
const Horse = ({ neighbors, onWin }) => {
  const [lanes, setLanes] = useState(() => neighbors.slice(0, 4));
  const [pos, setPos] = useState(() => lanes.map(() => 0));
  const [racing, setRacing] = useState(false);
  const [pickFor, setPickFor] = useState(null);
  const done = useRef(false);

  const start = () => {
    if (racing || lanes.length === 0) return;
    setRacing(true); done.current = false; setPos(lanes.map(() => 0));
    const iv = setInterval(() => {
      setPos((prev) => {
        const next = prev.map((p) => p + Math.random() * 6);
        const winner = next.findIndex((p) => p >= 100);
        if (winner >= 0 && !done.current) { done.current = true; clearInterval(iv); setRacing(false); celebrate(); setTimeout(() => onWin(lanes[winner]), 400); }
        return next.map((p) => Math.min(100, p));
      });
    }, 120);
  };

  return (
    <div className="px-4 pt-5">
      <p className="text-center font-bold text-ink mb-4">{lanes.length} horses · dash to the finish · winner gets your gift</p>
      <div className="rounded-lg overflow-hidden border-2 border-[#1B5E3F]" style={{ background: "#2E7D32" }}>
        {lanes.map((n, i) => (
          <div key={i} className="relative flex items-center gap-3 px-3 h-20 border-b border-white/10 last:border-0">
            <span className="font-black text-white w-4">{i + 1}</span>
            <div className="absolute right-2 top-0 bottom-0 w-2 border-l-2 border-dashed border-white/70" />
            <button data-testid={`horse-lane-${i}`} disabled={racing} onClick={() => setPickFor(i)}
              className="absolute flex items-center gap-1 transition-all disabled:cursor-default" style={{ left: `calc(${pos[i]}% * 0.82 + 40px)`, cursor: racing ? "default" : "pointer" }}>
              <CircleAvatar src={n?.picture} name={n?.name} size={38} ring="#ffffff" />
              <span className="text-2xl" style={{ transform: "scaleX(-1)" }}>🐎</span>
            </button>
          </div>
        ))}
      </div>
      <Button data-testid="horse-start" disabled={racing} onClick={start} className="w-full h-14 mt-5 rounded-pill font-black text-lg bg-brand-primary text-white">
        {racing ? "Racing…" : "They're off! 🏁"}
      </Button>
      <p className="text-center text-ink-tertiary font-semibold mt-4">Tap a horse's face to swap in a different neighbor.</p>

      <NeighborPicker open={pickFor !== null} onClose={() => setPickFor(null)} pool={neighbors} recipient={lanes[pickFor]} radiusLabel={getRadiusLabel()}
        onPick={(item) => { setLanes((l) => l.map((x, i) => (i === pickFor ? item : x))); setPickFor(null); }} />
    </div>
  );
};

// ---------- Deal or No Deal ----------
const DOND_VALUES = [1, 5, 10, 20, 30, 50, 75, 100, 150, 200, 300, 500, 750, 1000, 1500, 2500];
const ROUND_OPENS = [5, 3, 2, 2, 1, 1, 1];
const BANKER_FACTORS = [0.30, 0.45, 0.60, 0.72, 0.85, 0.95, 1.00];
const PUNISH_MULTIPLIER = 0.85;
const HANGUP_CHANCE = 0.5;
const fmt = (v) => `$${Number(v).toLocaleString()}`;
const computeOffer = (rem, factor, punished) => {
  if (rem.length === 0) return 0;
  const avg = rem.reduce((s, v) => s + v, 0) / rem.length;
  return Math.max(1, Math.round(avg * factor * (punished ? PUNISH_MULTIPLIER : 1)));
};

const dondBtn = "flex-1 py-3.5 rounded-pill font-black text-[15px] tracking-wide active:scale-95 transition-transform";

const DealOrNoDeal = ({ neighbors, onWin }) => {
  const [recipient, setRecipient] = useState(() => neighbors[Math.floor(Math.random() * Math.max(1, neighbors.length))]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [phase, setPhase] = useState("picking-own");
  const [cases, setCases] = useState(() => shuffle(DOND_VALUES).map((v, i) => ({ n: i + 1, value: v, opened: false })));
  const [ownCase, setOwnCase] = useState(null);
  const [round, setRound] = useState(0);
  const [openedThisRound, setOpened] = useState(0);
  const [offer, setOffer] = useState(null);
  const [offerHistory, setOfferHistory] = useState([]);
  const [bestRejected, setBestRejected] = useState(0);
  const [topBeforeRound, setTopBefore] = useState([]);
  const [lastRedFlashN, setRedFlash] = useState(null);
  const [finalAmount, setFinalAmount] = useState(null);
  const [finalWasDeal, setFinalWasDeal] = useState(false);
  const [bankerClosed, setBankerClosed] = useState(false);

  const radiusLabel = (() => {
    const stored = localStorage.getItem("blessed_radius");
    const rad = stored !== null ? JSON.parse(stored) : 100;
    return rad == null ? "any distance" : `${rad} mi`;
  })();

  const resetGame = () => {
    setCases(shuffle(DOND_VALUES).map((v, i) => ({ n: i + 1, value: v, opened: false })));
    setOwnCase(null); setRound(0); setOpened(0);
    setOffer(null); setOfferHistory([]); setBestRejected(0);
    setTopBefore([]); setRedFlash(null);
    setFinalAmount(null); setFinalWasDeal(false); setBankerClosed(false);
    setPhase("picking-own");
  };

  const onPickCase = (n) => {
    if (phase === "picking-own") {
      setOwnCase(n);
      setPhase("opening");
      setTopBefore(cases.filter((k) => k.n !== n).map((k) => k.value).sort((a, b) => b - a).slice(0, 3));
      return;
    }
    if (phase !== "opening" || n === ownCase) return;
    const slot = cases.find((k) => k.n === n);
    if (!slot || slot.opened) return;
    const next = cases.map((k) => (k.n === n ? { ...k, opened: true } : k));
    setCases(next);
    const nextOpened = openedThisRound + 1;
    const goal = ROUND_OPENS[round];
    if (topBeforeRound.includes(slot.value)) { setRedFlash(n); setTimeout(() => setRedFlash(null), 900); }
    if (nextOpened >= goal) {
      const stillInPlay = next.filter((k) => !k.opened).map((k) => k.value);
      const bigCaseKilled = topBeforeRound.some((v) => !stillInPlay.includes(v));
      const banker = computeOffer(stillInPlay, BANKER_FACTORS[round], bigCaseKilled);
      setTopBefore(next.filter((k) => !k.opened && k.n !== ownCase).map((k) => k.value).sort((a, b) => b - a).slice(0, 3));
      setOpened(0);
      setOffer(banker);
      const isLast = round >= ROUND_OPENS.length - 1;
      if (isLast) {
        const own = next.find((k) => k.n === ownCase);
        const amt = own?.value ?? 1;
        setFinalAmount(amt); setFinalWasDeal(false); setPhase("final");
        if (amt >= bestRejected) celebrate();
      } else setPhase("banker");
    } else setOpened(nextOpened);
  };

  const onDeal = () => {
    if (offer == null) return;
    setFinalAmount(offer); setFinalWasDeal(true);
    setOfferHistory((h) => [...h, offer]); setPhase("final"); celebrate();
  };
  const onNoDeal = () => {
    if (offer == null) return;
    setOfferHistory((h) => [...h, offer]);
    if (offer > bestRejected) setBestRejected(offer);
    setOffer(null);
    if (Math.random() < HANGUP_CHANCE) { setBankerClosed(true); setFinalAmount(0); setFinalWasDeal(false); setPhase("final"); return; }
    setRound((r) => r + 1); setPhase("opening");
  };

  const eliminated = new Set(cases.filter((k) => k.opened).map((k) => k.value));
  const lowHalf = DOND_VALUES.slice(0, DOND_VALUES.length / 2);
  const highHalf = DOND_VALUES.slice(DOND_VALUES.length / 2);
  const firstName = recipient?.name?.split(" ")[0] || "them";
  const remainThisRound = ROUND_OPENS[round] - openedThisRound;
  const banner =
    phase === "picking-own" ? "Pick your case ▸ tap any briefcase to keep it" :
    phase === "opening" ? `Open ${remainThisRound} more case${remainThisRound === 1 ? "" : "s"}` :
    phase === "banker" ? "The Banker is on the line…" :
    bankerClosed ? "The Banker hung up 📵" : finalWasDeal ? "DEAL ✅" : "Your case revealed";

  return (
    <div className="max-w-md mx-auto px-4 pt-4">
      {/* recipient (tap to change) */}
      {recipient && (
        <button data-testid="dond-recipient" onClick={() => phase !== "final" && setPickerOpen(true)}
          className="w-full flex items-center gap-3 p-3 bg-surface-2 border border-border rounded-lg mb-3 text-left">
          <CircleAvatar src={recipient.picture} name={recipient.name} size={48} />
          <div className="flex-1 min-w-0">
            <div className="font-black text-ink truncate">Playing to bless {recipient.name}</div>
            <div className="text-xs text-ink-tertiary truncate">{[recipient.place, phase !== "final" ? "tap to change" : null].filter(Boolean).join(" · ")}</div>
          </div>
        </button>
      )}

      {/* banner */}
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-pill mb-3 font-extrabold text-sm" style={{ background: "#FEF3C7", border: "1px solid #F59E0B", color: "#92400E" }}>
        ✨ {banner}
      </div>

      {/* prize board */}
      <div className="flex gap-2 mb-3">
        {[lowHalf, highHalf].map((half, idx) => (
          <div key={idx} className="flex-1 border border-border rounded-lg p-2 grid gap-1.5">
            {half.map((v) => {
              const gone = eliminated.has(v);
              const isLow = idx === 0;
              return (
                <div key={v} className="px-2 py-1 rounded-pill text-center font-black text-[13px]"
                  style={{
                    background: gone ? "transparent" : isLow ? "#DCFCE7" : "#FEE2E2",
                    border: gone ? "1px solid var(--border)" : "none",
                    color: gone ? "#9ca3af" : isLow ? "#166534" : "#991B1B",
                    textDecoration: gone ? "line-through" : "none",
                  }}>{fmt(v)}</div>
              );
            })}
          </div>
        ))}
      </div>

      {/* banker card */}
      {phase === "banker" && offer != null && (
        <div className="rounded-3xl p-5 text-center mb-3 shadow-2xl" style={{ background: "#1B1233", color: "#fff" }} data-testid="dond-banker">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-pill grid place-items-center" style={{ background: "#FF6B6B" }}>📞</div>
            <div className="text-xs font-black tracking-[2px]">THE BANKER</div>
          </div>
          <div className="text-4xl font-black" style={{ color: "#FDE047" }}>{fmt(offer)}</div>
          {offerHistory.length > 0 && (() => {
            const prev = offerHistory[offerHistory.length - 1];
            const delta = offer - prev; const dropped = delta < 0;
            return <div className="text-xs font-black mt-0.5" style={{ color: dropped ? "#FCA5A5" : "#86EFAC" }}>{dropped ? "▼" : "▲"} {fmt(Math.abs(delta))} vs last offer</div>;
          })()}
          <div className="text-[13px] opacity-85 mt-1 mb-2">
            {offerHistory.length > 0 && offer < offerHistory[offerHistory.length - 1] ? "That last case hurt. Take it before it drops more." : `My offer to bless ${firstName}`}
          </div>
          <div className="text-[11px] font-extrabold mb-3" style={{ color: "#FCA5A5" }}>⚠️ 50% chance I hang up if you say No Deal</div>
          <div className="flex gap-2.5">
            <button data-testid="dond-deal" onClick={onDeal} className={dondBtn} style={{ background: "#22C55E", color: "#fff" }}>DEAL</button>
            <button data-testid="dond-no-deal" onClick={onNoDeal} className={dondBtn} style={{ background: "#EF4444", color: "#fff" }}>NO DEAL</button>
          </div>
        </div>
      )}

      {/* final card */}
      {phase === "final" && finalAmount != null && (() => {
        const isLoss = finalAmount < bestRejected && !finalWasDeal;
        const bg = bankerClosed ? "#111827" : isLoss ? "#EF4444" : "#22C55E";
        const eyebrow = bankerClosed ? "GAME OVER 📵" : finalWasDeal ? "DEAL ✅" : isLoss ? "TOO GREEDY 💸" : "YOU BEAT THE BANKER 🎉";
        const sub = bankerClosed ? `The Banker slammed the phone. You rejected ${fmt(bestRejected)} and walk away with nothing this round.`
          : isLoss ? `You rejected ${fmt(bestRejected)} — your case only had ${fmt(finalAmount)}. Still, ${firstName} needs a blessing.`
          : finalWasDeal ? `You locked in the Banker's offer. Bless ${firstName} with ${fmt(finalAmount)}?`
          : `You held out and won bigger than any offer! Bless ${firstName} with ${fmt(finalAmount)}?`;
        return (
          <div className="rounded-3xl p-5 text-center mb-3 shadow-2xl" data-testid="dond-final"
            style={{ background: bg, color: "#fff", border: `2px solid ${isLoss ? "#7F1D1D" : bankerClosed ? "#000" : "#166534"}` }}>
            <div className="text-xs font-black tracking-[2px] opacity-90">{eyebrow}</div>
            <div className="text-5xl font-black my-1" style={{ color: bankerClosed ? "#FCA5A5" : "#fff" }}>{bankerClosed ? "$0" : fmt(finalAmount)}</div>
            {bestRejected > 0 && !finalWasDeal && !bankerClosed && (
              <div className="text-xs opacity-90 mb-1.5">Best banker offer you rejected: <strong>{fmt(bestRejected)}</strong></div>
            )}
            <div className="text-[13px] opacity-95 mb-3.5">{sub}</div>
            <div className="flex gap-2.5">
              {!bankerClosed && (
                <button data-testid="dond-send" onClick={() => onWin(recipient, finalAmount)} className={dondBtn} style={{ background: "#fff", color: "#1B1233" }}>Send {fmt(finalAmount)}</button>
              )}
              <button data-testid="dond-play-again" onClick={resetGame} className={dondBtn}
                style={bankerClosed ? { background: "#fff", color: "#111827" } : { background: "transparent", color: "#fff", border: "2px solid #fff" }}>Play again</button>
            </div>
          </div>
        );
      })()}

      {/* case grid (hidden during banker phase) */}
      {phase !== "banker" && (
        <div className="grid grid-cols-4 gap-2">
          {cases.map((k) => {
            const isOwn = k.n === ownCase;
            const isOpen = k.opened;
            const isBigFlash = lastRedFlashN === k.n;
            const disabled = phase === "final" || isOpen || (phase === "opening" && isOwn);
            let bg = "#7C3AED";
            if (isOpen) bg = isBigFlash ? "#EF4444" : "#E5E7EB";
            if (isOwn && !isOpen) bg = "#FDE047";
            if (isOwn && phase === "final") bg = "#22C55E";
            const textColor = isOpen && !isBigFlash ? "#1B1233" : "#fff";
            return (
              <button key={k.n} data-testid={`dond-case-${k.n}`} onClick={() => !disabled && onPickCase(k.n)} disabled={disabled}
                className="aspect-square rounded-md font-black flex flex-col items-center justify-center gap-0.5 shadow-md disabled:cursor-default active:scale-95 transition-transform"
                style={{ background: bg, color: textColor, opacity: isOpen && !isBigFlash ? 0.65 : 1,
                  border: isOwn && !isOpen ? "3px solid #1B1233" : isBigFlash ? "2px solid #7F1D1D" : "none" }}>
                {isOpen ? (
                  <><div className="text-[10px]">#{k.n}</div><div style={{ fontSize: isBigFlash ? 15 : 13 }}>{fmt(k.value)}</div></>
                ) : (
                  <><div className="text-[22px]">💼</div><div className="text-lg">{k.n}</div>{isOwn && <div className="text-[9px] tracking-widest" style={{ color: "#1B1233" }}>YOURS</div>}</>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* offer history */}
      {offerHistory.length > 0 && phase !== "final" && (
        <div className="mt-3 p-3 border border-border rounded-lg">
          <div className="text-[11px] font-extrabold tracking-widest text-ink-tertiary mb-1.5">PREVIOUS OFFERS (REJECTED)</div>
          <div className="flex flex-wrap gap-1.5">
            {offerHistory.map((o, i) => <div key={i} className="bg-surface-3 rounded-pill px-2.5 py-1 text-xs font-extrabold text-ink">{fmt(o)}</div>)}
          </div>
        </div>
      )}

      <NeighborPicker open={pickerOpen} onClose={() => setPickerOpen(false)} pool={neighbors} recipient={recipient} radiusLabel={radiusLabel}
        onPick={(item) => { setRecipient(item); setPickerOpen(false); }} />
    </div>
  );
};

// ---------- Container ----------
export default function GamePlay() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const coords = useMemo(() => userCoords(user), [user]);
  const [neighbors, setNeighbors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seed, setSeed] = useState(0);
  const [round, setRound] = useState(0);
  const meta = TITLES[gameId] || TITLES.wheel;

  const loadNeighbors = () => {
    // Respect the radius the user picked on the Map (persisted in localStorage; null = ∞).
    const stored = localStorage.getItem("blessed_radius");
    const rad = stored !== null ? JSON.parse(stored) : 100;
    const params = { limit: 20, radius_miles: rad == null ? UNLIMITED_RADIUS : rad };
    if (coords) { params.lat = coords.lat; params.lng = coords.lng; }
    api.get("/recipients/nearby", { params })
      .then(({ data }) => {
        const list = shuffle((Array.isArray(data) ? data : data.recipients || data.items || []).map(normRecipient).filter((r) => !r.isMe));
        setNeighbors(list);
      })
      .catch(() => setNeighbors([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadNeighbors(); /* eslint-disable-next-line */ }, [coords]);

  const go = (recipient, amount) => {
    const q = new URLSearchParams({ message: "A little something from a game I played 🎲" });
    if (amount) q.set("amount", String(amount));
    navigate(`/donate/${recipient.id}?${q.toString()}`);
  };
  // Always open the full donate page (Card + Zelle/Venmo) — no small amount popup.
  const onWin = (recipient, amount) => go(recipient, amount);

  const targetIdx = seed % Math.max(1, neighbors.length);

  return (
    <div className="max-w-2xl mx-auto pb-28 min-h-screen">
      <div className="relative flex items-center justify-center px-4 py-4 border-b border-border">
        <button data-testid="gameplay-back" onClick={() => navigate("/games")} className="absolute left-4 w-10 h-10 rounded-pill grid place-items-center bg-surface-3 text-ink-tertiary"><ArrowLeft size={20} /></button>
        <h1 className="text-xl font-black">{meta.name} <span>{meta.emoji}</span></h1>
        <button data-testid="gameplay-shuffle" onClick={() => { setSeed((s) => s + 1); loadNeighbors(); }} className="absolute right-4 w-10 h-10 rounded-pill grid place-items-center bg-surface-3 text-ink-tertiary"><RotateCw size={18} /></button>
      </div>

      {loading ? (
        <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-brand-primary" size={32} /></div>
      ) : neighbors.length === 0 ? (
        <div className="py-24 text-center px-8"><p className="font-black text-lg">No neighbors nearby</p><p className="text-ink-tertiary">Try widening your area from the Map.</p></div>
      ) : (
        <>
          {gameId === "wheel" && <Wheel key={`${seed}-${round}`} neighbors={neighbors} onWin={onWin} />}
          {gameId === "slots" && <Slots key={`${seed}-${round}`} neighbors={neighbors} onWin={onWin} />}
          {gameId === "blackjack" && <Blackjack key={`${seed}-${round}`} neighbors={neighbors} targetIdx={targetIdx} onWin={onWin} />}
          {gameId === "horse" && <Horse key={`${seed}-${round}`} neighbors={neighbors} onWin={onWin} />}
          {gameId === "deal" && <DealOrNoDeal key={`${seed}-${round}`} neighbors={neighbors} onWin={onWin} />}
        </>
      )}
    </div>
  );
}
